// Quality Assurance tab (B6) — the review working drawer. Draft reviews edit
// the scope (admin-managed areas, each with a 5-state status + a checklist of
// items marked Verified / Issue / N/A), the findings list (area + severity
// Observation|Exception + description) and the narrative. Completion is NOT
// blocked: a hint surfaces how many Required checklist items are unaddressed,
// and completing stamps the review Full or Partial. An Exception finding
// offers "Create follow-up task" (saves the draft first). FIND-12: every
// field hydrates from the review and saves send the full payload.

import { useMemo, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCompleteQaReview,
  useCreateQaFollowUpTask,
  useGenerateQaReport,
  useUpdateQaReview,
} from "@/hooks/portfolioAdmin/useQaReviews";
import {
  QA_CHECKLIST_STATUS_LABELS,
  QA_SCOPE_STATE_LABELS,
  type LoanQaReviewDto,
  type QaChecklistStatus,
  type QaFindingSeverity,
  type QaScopeState,
} from "@/api/portfolioAdmin/qaReviewsApi";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { formatDate } from "../common/colorBand";
import { QaSeverityBadge } from "./qaShared";

interface FindingDraft {
  id: string;
  area: string;
  severity: QaFindingSeverity;
  description: string;
  followUpTaskId: string | null;
  followUpTaskStatus: string | null;
}

interface AreaState {
  state: QaScopeState;
  checklist: Record<string, QaChecklistStatus>;
}

const SCOPE_STATES = Object.keys(QA_SCOPE_STATE_LABELS) as QaScopeState[];
const CHECKLIST_STATUSES = Object.keys(
  QA_CHECKLIST_STATUS_LABELS,
) as QaChecklistStatus[];

interface QaReviewDrawerProps {
  loanId: string;
  review: LoanQaReviewDto;
  canAct: boolean;
  open: boolean;
  onClose: () => void;
  onOpenReport: (reportId: string) => void;
}

export function QaReviewDrawer({
  loanId,
  review,
  canAct,
  open,
  onClose,
  onOpenReport,
}: QaReviewDrawerProps) {
  const toast = useToast();
  const update = useUpdateQaReview(loanId);
  const complete = useCompleteQaReview(loanId);
  const createTask = useCreateQaFollowUpTask(loanId);
  const generateReport = useGenerateQaReport(loanId);

  const isDraft = review.status === "Draft";
  const editable = isDraft && canAct;

  // Hydrate from the review (FIND-12). Areas + checklist items are
  // server-owned; we only track per-area state + per-item status.
  const [areaState, setAreaState] = useState<Record<string, AreaState>>(() =>
    Object.fromEntries(
      review.scope.map((a) => [
        a.area,
        {
          state: a.state,
          checklist: Object.fromEntries(
            a.checklist.map((c) => [c.itemCode, c.status]),
          ),
        },
      ]),
    ),
  );
  const [findings, setFindings] = useState<FindingDraft[]>(() =>
    review.findings.map((f) => ({ ...f })),
  );
  const [narrative, setNarrative] = useState(review.narrative ?? "");
  const [error, setError] = useState<string | null>(null);

  const setState = (area: string, state: QaScopeState) =>
    setAreaState((prev) => ({ ...prev, [area]: { ...prev[area], state } }));
  const setItemStatus = (area: string, itemCode: string, status: QaChecklistStatus) =>
    setAreaState((prev) => ({
      ...prev,
      [area]: {
        ...prev[area],
        checklist: { ...prev[area].checklist, [itemCode]: status },
      },
    }));

  // Live unresolved-Required count, so the reviewer completes knowingly.
  const unresolvedRequired = useMemo(
    () =>
      review.scope.reduce(
        (sum, a) =>
          sum +
          a.checklist.filter(
            (c) => c.required && !(areaState[a.area]?.checklist[c.itemCode] ?? ""),
          ).length,
        0,
      ),
    [review.scope, areaState],
  );

  const buildBody = () => ({
    scope: review.scope.map((a) => ({
      area: a.area,
      state: areaState[a.area]?.state ?? "NotExamined",
      checklist: a.checklist.map((c) => ({
        itemCode: c.itemCode,
        status: areaState[a.area]?.checklist[c.itemCode] ?? "",
      })),
    })),
    findings: findings.map((f) => ({
      id: f.id,
      area: f.area,
      severity: f.severity,
      description: f.description,
    })),
    narrative: narrative.trim() === "" ? null : narrative.trim(),
  });

  const saveDraft = async () => {
    setError(null);
    try {
      await update.mutateAsync({ reviewId: review.id, body: buildBody() });
      toast.success("Review saved");
      return true;
    } catch (err) {
      setError(extractErrorMessage(err));
      return false;
    }
  };

  const completeReview = async () => {
    if (!(await saveDraft())) return;
    try {
      await complete.mutateAsync(review.id);
      toast.success("Review completed");
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const createFollowUp = async (findingId: string) => {
    if (isDraft && !(await saveDraft())) return;
    setError(null);
    try {
      await createTask.mutateAsync({ reviewId: review.id, findingId });
      toast.success("Follow-up task created");
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const generate = async () => {
    setError(null);
    try {
      await generateReport.mutateAsync(review.id);
      toast.success("QA report generated");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const pending =
    update.isPending || complete.isPending || createTask.isPending ||
    generateReport.isPending;

  const inputClass =
    "mt-1 w-full border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure";
  const selectClass =
    "px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure";

  const findingAreaOptions = review.scope.map((a) => ({
    value: a.area,
    label: a.name,
  }));

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      width="lg"
      title={isDraft ? "QA review (draft)" : "QA review"}
      subtitle={`${formatDate(review.reviewDate)} · ${review.reviewedByUserName ?? "Unknown reviewer"}`}
      testId="qa-review-drawer"
      footer={
        <>
          {error && (
            <p
              className="text-caption text-status-error m-0 mr-auto"
              role="alert"
              data-testid="qa-review-error"
            >
              {error}
            </p>
          )}
          {editable && unresolvedRequired > 0 && !error && (
            <p
              className="text-caption text-status-warning m-0 mr-auto"
              data-testid="qa-unresolved-required"
            >
              {unresolvedRequired} required item
              {unresolvedRequired === 1 ? "" : "s"} unaddressed — completing marks
              this review Partial.
            </p>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {editable && (
            <>
              <Button
                variant="secondary"
                onClick={() => void saveDraft()}
                loading={update.isPending}
                data-testid="qa-review-save"
              >
                Save draft
              </Button>
              <Button
                variant="primary"
                onClick={() => void completeReview()}
                loading={pending}
                data-testid="qa-review-complete"
              >
                Complete review
              </Button>
            </>
          )}
          {!isDraft && canAct && !review.reportLoanReportId && (
            <Button
              variant="primary"
              onClick={() => void generate()}
              loading={generateReport.isPending}
              data-testid="qa-review-generate-report"
            >
              Generate QA report
            </Button>
          )}
          {!isDraft && review.reportLoanReportId && (
            <Button
              variant="primary"
              onClick={() => onOpenReport(review.reportLoanReportId!)}
              data-testid="qa-review-open-report"
            >
              Open report
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        {/* Scope — per area: 5-state status + checklist */}
        <section aria-label="Scope of review">
          <h3 className="font-display font-bold text-small uppercase tracking-label text-gray-mid m-0 mb-space-2">
            Scope — areas examined
          </h3>
          {review.scope.length === 0 ? (
            <p className="text-small text-gray-mid m-0" data-testid="qa-no-scope">
              No scope areas are configured.
            </p>
          ) : (
            <div className="flex flex-col gap-space-3">
              {review.scope.map((area) => (
                <div
                  key={area.area}
                  className="rounded-md border border-border-light p-space-2"
                  data-testid={`qa-area-${area.area}`}
                >
                  <div className="flex items-start justify-between gap-space-3">
                    <div className="min-w-0">
                      <div className="text-small font-semibold text-onyx">
                        {area.name}
                      </div>
                      {area.purpose && (
                        <p className="text-caption text-gray-mid m-0 mt-[2px]">
                          {area.purpose}
                        </p>
                      )}
                    </div>
                    {editable ? (
                      <select
                        aria-label={`Status for ${area.name}`}
                        className={selectClass}
                        value={areaState[area.area]?.state ?? "NotExamined"}
                        onChange={(e) =>
                          setState(area.area, e.target.value as QaScopeState)
                        }
                        data-testid={`qa-scope-${area.area}`}
                      >
                        {SCOPE_STATES.map((s) => (
                          <option key={s} value={s}>
                            {QA_SCOPE_STATE_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-small text-gray-mid whitespace-nowrap">
                        {QA_SCOPE_STATE_LABELS[area.state]}
                      </span>
                    )}
                  </div>
                  {area.checklist.length > 0 && (
                    <ul className="m-0 mt-space-2 list-none p-0 flex flex-col gap-space-1">
                      {area.checklist.map((item) => (
                        <li
                          key={item.itemCode}
                          className="flex items-center justify-between gap-space-3"
                          data-testid={`qa-item-${item.itemCode}`}
                        >
                          <span className="text-caption text-onyx">
                            {item.label}
                            {item.required && (
                              <span className="text-status-error"> *</span>
                            )}
                            {item.helpText && (
                              <span className="block text-gray-mid">
                                {item.helpText}
                              </span>
                            )}
                          </span>
                          {editable ? (
                            <select
                              aria-label={`Status for ${item.label}`}
                              className={selectClass}
                              value={
                                areaState[area.area]?.checklist[item.itemCode] ?? ""
                              }
                              onChange={(e) =>
                                setItemStatus(
                                  area.area,
                                  item.itemCode,
                                  e.target.value as QaChecklistStatus,
                                )
                              }
                              data-testid={`qa-item-status-${item.itemCode}`}
                            >
                              {CHECKLIST_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {QA_CHECKLIST_STATUS_LABELS[s]}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-caption text-gray-mid whitespace-nowrap">
                              {QA_CHECKLIST_STATUS_LABELS[item.status]}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Findings */}
        <section aria-label="Findings">
          <div className="flex items-center justify-between gap-space-3 mb-space-2">
            <h3 className="font-display font-bold text-small uppercase tracking-label text-gray-mid m-0">
              Findings
            </h3>
            {editable && findingAreaOptions.length > 0 && (
              <Button
                variant="tertiary"
                size="sm"
                onClick={() =>
                  setFindings((f) => [
                    ...f,
                    {
                      id: crypto.randomUUID(),
                      area: findingAreaOptions[0].value,
                      severity: "Observation",
                      description: "",
                      followUpTaskId: null,
                      followUpTaskStatus: null,
                    },
                  ])
                }
                data-testid="qa-add-finding"
              >
                + Add finding
              </Button>
            )}
          </div>
          {findings.length === 0 ? (
            <p className="text-small text-gray-mid m-0" data-testid="qa-no-findings">
              No findings recorded.
            </p>
          ) : (
            <div className="flex flex-col gap-space-2">
              {findings.map((f, idx) => (
                <div
                  key={f.id}
                  className="rounded-md border border-border-light p-space-2"
                  data-testid={`qa-finding-${f.id}`}
                >
                  {editable ? (
                    <div className="flex flex-col gap-space-2">
                      <div className="flex flex-wrap items-center gap-space-2">
                        <select
                          aria-label="Finding area"
                          className={selectClass}
                          value={f.area}
                          onChange={(e) =>
                            setFindings((all) =>
                              all.map((x, i) =>
                                i === idx ? { ...x, area: e.target.value } : x,
                              ),
                            )
                          }
                          data-testid={`qa-finding-area-${f.id}`}
                        >
                          {findingAreaOptions.map((a) => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </select>
                        <select
                          aria-label="Finding severity"
                          className={selectClass}
                          value={f.severity}
                          disabled={!!f.followUpTaskId}
                          onChange={(e) =>
                            setFindings((all) =>
                              all.map((x, i) =>
                                i === idx
                                  ? {
                                      ...x,
                                      severity: e.target.value as QaFindingSeverity,
                                    }
                                  : x,
                              ),
                            )
                          }
                          data-testid={`qa-finding-severity-${f.id}`}
                        >
                          <option value="Observation">Observation</option>
                          <option value="Exception">Exception</option>
                        </select>
                        {!f.followUpTaskId && (
                          <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() =>
                              setFindings((all) => all.filter((_, i) => i !== idx))
                            }
                            aria-label="Remove finding"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <textarea
                        aria-label="Finding description"
                        className={`${inputClass} min-h-[56px]`}
                        value={f.description}
                        onChange={(e) =>
                          setFindings((all) =>
                            all.map((x, i) =>
                              i === idx ? { ...x, description: e.target.value } : x,
                            ),
                          )
                        }
                        data-testid={`qa-finding-description-${f.id}`}
                      />
                    </div>
                  ) : (
                    <div className="flex items-start gap-space-2">
                      <QaSeverityBadge severity={f.severity} />
                      <div className="min-w-0">
                        <div className="text-small text-onyx">
                          <span className="font-semibold">
                            {findingAreaOptions.find((a) => a.value === f.area)
                              ?.label ?? f.area}
                          </span>{" "}
                          — {f.description}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-space-2 flex items-center gap-space-2">
                    {f.followUpTaskId ? (
                      <span
                        className="text-caption text-gray-mid"
                        data-testid={`qa-finding-task-${f.id}`}
                      >
                        Follow-up task:{" "}
                        {f.followUpTaskStatus === "Done" ? "completed" : "open"}
                      </span>
                    ) : (
                      f.severity === "Exception" &&
                      canAct && (
                        <Button
                          variant="tertiary"
                          size="sm"
                          onClick={() => void createFollowUp(f.id)}
                          loading={createTask.isPending}
                          disabled={f.description.trim() === ""}
                          data-testid={`qa-finding-create-task-${f.id}`}
                        >
                          Create follow-up task
                        </Button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Narrative */}
        <section aria-label="Narrative">
          <h3 className="font-display font-bold text-small uppercase tracking-label text-gray-mid m-0 mb-space-2">
            Narrative
          </h3>
          {editable ? (
            <textarea
              aria-label="Review narrative"
              className={`${inputClass} min-h-[88px]`}
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              data-testid="qa-narrative"
            />
          ) : (
            <p className="text-small text-onyx whitespace-pre-wrap m-0">
              {review.narrative ?? "No narrative recorded."}
            </p>
          )}
        </section>
      </div>
    </ResourceDrawer>
  );
}
